import { useState, useEffect } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { subscribeToPush } from '../hooks/usePushNotifications';

const isNative = Capacitor.isNativePlatform();

const NativeGps = isNative
  ? registerPlugin<{
      requestBatteryOptimization(): Promise<{ requested: boolean }>;
      openAppSettings(): Promise<{ opened: boolean }>;
      isBatteryOptimized(): Promise<{ optimized: boolean; unrestricted: boolean }>;
    }>('NativeGps')
  : null;

// --- SVG Icons ---
function LocationIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
      <circle cx="12" cy="9" r="2.5" />
      <path d="M12 2c0 0-2.5-0.5-2.5-0.5" opacity="0.4" />
    </svg>
  );
}

function BackgroundLocationIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
      <circle cx="12" cy="9" r="2" />
      <path d="M20 12a8 8 0 0 1-8 8" strokeDasharray="3 3" />
      <path d="M4 12a8 8 0 0 1 8-8" strokeDasharray="3 3" />
    </svg>
  );
}

function BatteryIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="18" height="12" rx="2" />
      <path d="M22 10v4" />
      <path d="M11 9l-2 3h4l-2 3" strokeWidth="2" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function NotificationIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      <path d="M2 2l2 2" opacity="0.5" />
      <path d="M22 2l-2 2" opacity="0.5" />
    </svg>
  );
}

function AlertShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
      <circle cx="12" cy="15.5" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

const ICON_MAP: Record<string, () => React.JSX.Element> = {
  location: LocationIcon,
  background_location: BackgroundLocationIcon,
  battery: BatteryIcon,
  camera: CameraIcon,
  notifications: NotificationIcon,
  fullscreen_alerts: AlertShieldIcon,
};

// --- Types ---
interface PermissionStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'granted' | 'denied' | 'skipped';
  hint?: string;
  driverOnly?: boolean;
}

interface PermissionSetupProps {
  isDriver: boolean;
  isParent?: boolean;
  onComplete: () => void;
}

export function PermissionSetup({ isDriver, isParent, onComplete }: PermissionSetupProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<PermissionStep[]>([]);

  useEffect(() => {
    const allSteps: PermissionStep[] = [
      {
        id: 'location',
        title: 'Location Access',
        description: isDriver
          ? 'To share your bus location with parents in real-time and enable route tracking.'
          : 'To show your child\'s bus on the map in real-time and calculate arrival times.',
        status: 'pending',
      },
      {
        id: 'background_location',
        title: 'Background Location',
        description: 'So GPS tracking continues when your screen is off during trips. Without this, parents won\'t see live updates.',
        status: 'pending',
        hint: 'Select "Allow all the time" when prompted.',
        driverOnly: true,
      },
      {
        id: 'battery',
        title: 'Unrestricted Battery',
        description: 'Prevents Android from stopping GPS updates in the background. This ensures continuous tracking throughout the trip.',
        status: 'pending',
        hint: 'Tap "Allow" on the popup to disable battery optimization.',
        driverOnly: true,
      },
      {
        id: 'camera',
        title: 'Camera Access',
        description: isDriver
          ? 'To scan student QR codes for attendance check-in when boarding and exiting the bus.'
          : 'To scan the bus QR code and verify it\'s the correct bus for your child.',
        status: 'pending',
      },
      {
        id: 'notifications',
        title: 'Push Notifications',
        description: isDriver
          ? 'To receive SOS alerts, trip reminders, and important messages from the school admin.'
          : 'To get alerts when the bus is near your stop, when your child boards, and in emergencies.',
        status: 'pending',
      },
      {
        id: 'fullscreen_alerts',
        title: 'Emergency Alerts',
        description: 'Allows SOS and emergency alerts to appear over your lock screen, even when the phone is in silent or Do Not Disturb mode.',
        status: 'pending',
        hint: 'This ensures you never miss a critical safety alert.',
      },
    ];

    const filtered = allSteps.filter(s => !s.driverOnly || isDriver);
    setSteps(filtered);
  }, [isDriver, isParent]);

  const updateStepStatus = (id: string, status: PermissionStep['status']) => {
    setSteps(prev => prev.map(s => s.id === id ? { ...s, status } : s));
  };

  const goNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      localStorage.setItem('permissions_setup_done', 'true');
      onComplete();
    }
  };

  const requestLocation = async () => {
    try {
      if (isNative) {
        const perm = await Geolocation.requestPermissions();
        if (perm.location === 'granted' || perm.coarseLocation === 'granted') {
          updateStepStatus('location', 'granted');
          goNext();
          return;
        }
      } else {
        navigator.geolocation.getCurrentPosition(
          () => { updateStepStatus('location', 'granted'); goNext(); },
          () => { updateStepStatus('location', 'denied'); goNext(); },
          { enableHighAccuracy: true, timeout: 10000 }
        );
        return;
      }
    } catch {
      // Fall through
    }
    updateStepStatus('location', 'denied');
    goNext();
  };

  const requestBackgroundLocation = async () => {
    if (!isNative) {
      updateStepStatus('background_location', 'skipped');
      goNext();
      return;
    }
    try {
      const perm = await Geolocation.requestPermissions();
      if (perm.location === 'granted') {
        updateStepStatus('background_location', 'granted');
      } else {
        updateStepStatus('background_location', 'denied');
      }
    } catch {
      updateStepStatus('background_location', 'denied');
    }
    goNext();
  };

  const requestBatteryOptimization = async () => {
    if (!isNative || !NativeGps) {
      updateStepStatus('battery', 'skipped');
      goNext();
      return;
    }
    try {
      await NativeGps.requestBatteryOptimization();
      setTimeout(async () => {
        try {
          const result = await NativeGps!.isBatteryOptimized();
          updateStepStatus('battery', result.unrestricted ? 'granted' : 'denied');
        } catch {
          updateStepStatus('battery', 'granted');
        }
        goNext();
      }, 2000);
      return;
    } catch {
      updateStepStatus('battery', 'denied');
    }
    goNext();
  };

  const requestCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(t => t.stop());
      updateStepStatus('camera', 'granted');
    } catch {
      updateStepStatus('camera', 'denied');
    }
    goNext();
  };

  const requestNotifications = async () => {
    if (!('Notification' in window)) {
      updateStepStatus('notifications', 'skipped');
      goNext();
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') {
        updateStepStatus('notifications', 'granted');
        // Actually subscribe to push notifications (creates PushManager subscription + sends to server)
        await subscribeToPush();
      } else {
        updateStepStatus('notifications', 'denied');
      }
    } catch {
      updateStepStatus('notifications', 'denied');
    }
    goNext();
  };

  const requestFullscreenAlerts = async () => {
    // USE_FULL_SCREEN_INTENT is declared in manifest — informational step
    updateStepStatus('fullscreen_alerts', 'granted');
    goNext();
  };

  const handleRequest = async () => {
    const step = steps[currentStep];
    if (!step) return;

    switch (step.id) {
      case 'location': await requestLocation(); break;
      case 'background_location': await requestBackgroundLocation(); break;
      case 'battery': await requestBatteryOptimization(); break;
      case 'camera': await requestCamera(); break;
      case 'notifications': await requestNotifications(); break;
      case 'fullscreen_alerts': await requestFullscreenAlerts(); break;
    }
  };

  const handleSkip = () => {
    updateStepStatus(steps[currentStep].id, 'skipped');
    goNext();
  };

  if (steps.length === 0) return null;

  const step = steps[currentStep];
  const IconComponent = ICON_MAP[step.id];

  return (
    <div className="permission-setup-overlay">
      <div className="permission-setup-card">
        <div className="permission-setup-header">
          <div className="permission-app-icon">🚌</div>
          <h2>Welcome to Bus Tracker</h2>
          <p className="permission-setup-subtitle">
            We need a few permissions to keep {isDriver ? 'tracking running' : 'your child safe'}
          </p>
        </div>

        {/* Step dots */}
        <div className="permission-dots">
          {steps.map((s, i) => (
            <div
              key={s.id}
              className={`permission-dot ${
                i < currentStep
                  ? `dot-${s.status}`
                  : i === currentStep
                  ? 'dot-active'
                  : ''
              }`}
            />
          ))}
        </div>
        <p className="permission-step-count">Step {currentStep + 1} of {steps.length}</p>

        {/* Current step with slide animation */}
        <div className="permission-step" key={step.id}>
          <div className="permission-icon-container">
            {IconComponent && <IconComponent />}
          </div>
          <h3 className="permission-title">{step.title}</h3>
          <p className="permission-description">{step.description}</p>
          {step.hint && <p className="permission-hint">{step.hint}</p>}
        </div>

        {/* Completed steps summary */}
        {currentStep > 0 && (
          <div className="permission-completed">
            {steps.slice(0, currentStep).map(s => (
              <div key={s.id} className={`permission-status-badge status-${s.status}`}>
                <span>{s.status === 'granted' ? '✓' : s.status === 'denied' ? '✗' : '–'}</span>
                <span>{s.title}</span>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="permission-actions">
          <button className="btn-primary permission-allow-btn" onClick={handleRequest}>
            {step.id === 'fullscreen_alerts' ? 'Got it' : `Allow ${step.title}`}
          </button>
          {step.status === 'denied' && isNative && (
            <button className="permission-settings-btn" onClick={async () => {
              try { await NativeGps?.openAppSettings(); } catch { /* ignore */ }
            }} style={{ color: '#e94560', background: 'transparent', border: '1px solid #e94560', borderRadius: '8px', padding: '0.6rem 1.5rem', cursor: 'pointer', marginBottom: '0.5rem' }}>
              Open Settings
            </button>
          )}
          <button className="permission-skip-btn" onClick={handleSkip}>
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
